import { useEffect, useState } from "react";
import api, { API } from "@/lib/api";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Loader2, DollarSign, Users, Briefcase, FileText, Download, Printer, RefreshCw, Ban, Flag, CheckCircle2, FileDown, Share2, ImageDown, Sparkles, Copy } from "lucide-react";
import { Input } from "@/components/ui/input";

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [payments, setPayments] = useState([]);
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [social, setSocial] = useState(null); // {job, caption, theme}
  const [autoPost, setAutoPost] = useState(false);
  const [roundupTheme, setRoundupTheme] = useState("default");

  const [jd, setJd] = useState({ job_title: "", company: "", state: "", notes: "" });
  const [jdOut, setJdOut] = useState("");
  const [jdLoading, setJdLoading] = useState(false);
  const [cl, setCl] = useState({ job_title: "", company: "", background: "" });
  const [clOut, setClOut] = useState("");
  const [clLoading, setClLoading] = useState(false);

  const genJobDescription = async () => {
    if (!jd.notes.trim()) { toast.error("Add a few notes first"); return; }
    setJdLoading(true); setJdOut("");
    try {
      const { data } = await api.post("/ai/job-description", jd);
      setJdOut(data.text);
    } catch (e) { toast.error(e.response?.data?.detail || "Generation failed"); }
    finally { setJdLoading(false); }
  };

  const genCoverLetter = async () => {
    if (!cl.job_title.trim() || !cl.company.trim() || !cl.background.trim()) { toast.error("Fill job title, company and background"); return; }
    setClLoading(true); setClOut("");
    try {
      const { data } = await api.post("/ai/cover-letter", cl);
      setClOut(data.text);
    } catch (e) { toast.error(e.response?.data?.detail || "Generation failed"); }
    finally { setClLoading(false); }
  };

  const copyText = (t) => { navigator.clipboard.writeText(t); toast.success("Copied to clipboard"); };

  const openSocial = async (job) => {
    setSocial({ job, caption: "Loading caption…", theme: "default" });
    try {
      const { data } = await api.get(`/admin/social/job/${job.job_id}/caption`);
      setSocial({ job, caption: data.caption, theme: "default" });
    } catch { setSocial({ job, caption: "Could not load caption.", theme: "default" }); }
  };

  const toggleAutoPost = async (v) => {
    setAutoPost(v);
    try { await api.put("/admin/settings/auto-post", { enabled: v }); toast.success(v ? "Weekly auto-post enabled" : "Auto-post disabled"); }
    catch { toast.error("Could not update"); setAutoPost(!v); }
  };

  const loadAll = () => {
    setLoading(true);
    Promise.all([
      api.get("/admin/stats"), api.get("/admin/users"), api.get("/admin/jobs"),
      api.get("/admin/payments"), api.get("/admin/applications"),
    ]).then(([s, u, j, p, a]) => { setStats(s.data); setUsers(u.data); setJobs(j.data); setPayments(p.data); setApps(a.data); })
      .catch(() => toast.error("Failed to load admin data")).finally(() => setLoading(false));
    api.get("/admin/settings").then(({ data }) => setAutoPost(!!data.auto_post_enabled)).catch(() => {});
  };
  useEffect(() => { loadAll(); }, []);

  const moderate = async (jobId, status) => {
    try {
      await api.patch(`/admin/jobs/${jobId}/moderate`, { status });
      setJobs((prev) => prev.map((j) => (j.job_id === jobId ? { ...j, status } : j)));
      toast.success(`Job ${status}`);
    } catch {
      toast.error("Moderation failed");
      loadAll();
    }
  };
  const refund = async (sessionId) => {
    try { await api.post("/admin/refund", { session_id: sessionId }); toast.success("Refund issued"); loadAll(); }
    catch (e) { toast.error(e.response?.data?.detail || "Refund failed"); }
  };
  const resendEmail = async (id) => {
    try { await api.post(`/admin/applications/${id}/resend-email`); toast.success("Email resent"); loadAll(); }
    catch { toast.error("Resend failed"); }
  };
  const exportCsv = (dataset) => {
    const token = "";
    window.open(`${API}/admin/export/${dataset}.csv`, "_blank");
  };

  const money = (n) => `$${(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;

  if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-amber-500" /></div>;

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Header />
      <div className="px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto py-8 flex-1 w-full">
        <div className="flex items-center justify-between mb-8 flex-wrap gap-3">
          <div><h1 className="font-display text-3xl font-extrabold text-navy-800">Admin back office</h1><p className="text-slate-500 mt-1">Revenue, users, moderation and exports.</p></div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={loadAll} className="gap-2" data-testid="admin-refresh"><RefreshCw className="h-4 w-4" /> Refresh</Button>
            <Select value={roundupTheme} onValueChange={(v) => { setRoundupTheme(v); }}>
              <SelectTrigger className="w-[130px] h-9" data-testid="roundup-theme-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {["default", "newyear", "spring", "summer", "independence", "fall", "holiday"].map((t) => (
                  <SelectItem key={t} value={t}>{t === "default" ? "Auto theme" : t.charAt(0).toUpperCase() + t.slice(1)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={() => window.open(`${API}/admin/social/roundup.png${roundupTheme !== "default" ? `?theme=${roundupTheme}` : ""}`, "_blank")} className="gap-2" data-testid="admin-weekly-roundup"><ImageDown className="h-4 w-4" /> Weekly Roundup</Button>
            <label className="flex items-center gap-2 px-3 rounded-md border border-slate-200 bg-white text-sm font-medium text-navy-800" data-testid="autopost-toggle-wrap">
              <span>Auto-post Mondays</span>
              <Switch checked={autoPost} onCheckedChange={toggleAutoPost} data-testid="autopost-toggle" />
            </label>
            <Button variant="outline" onClick={() => window.open(`${API}/admin/export/revenue.pdf`, "_blank")} className="gap-2" data-testid="admin-export-pdf"><FileDown className="h-4 w-4" /> Revenue PDF</Button>
            <Button variant="outline" onClick={() => window.print()} className="gap-2" data-testid="admin-print"><Printer className="h-4 w-4" /> Print summary</Button>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <Stat icon={DollarSign} label="Total revenue" value={money(stats.total_revenue)} accent />
          <Stat icon={Briefcase} label="Active jobs" value={`${stats.active_jobs} / ${stats.total_jobs}`} />
          <Stat icon={Users} label="Employers / Applicants" value={`${stats.total_employers} / ${stats.total_applicants}`} />
          <Stat icon={FileText} label="Applications" value={stats.total_applications} />
        </div>

        <Tabs defaultValue="revenue">
          <TabsList className="flex-wrap h-auto">
            <TabsTrigger value="revenue" data-testid="admin-tab-revenue">Revenue</TabsTrigger>
            <TabsTrigger value="jobs" data-testid="admin-tab-jobs">Jobs & Moderation</TabsTrigger>
            <TabsTrigger value="users" data-testid="admin-tab-users">Users</TabsTrigger>
            <TabsTrigger value="payments" data-testid="admin-tab-payments">Payments</TabsTrigger>
            <TabsTrigger value="apps" data-testid="admin-tab-apps">Applications</TabsTrigger>
            <TabsTrigger value="ai" data-testid="admin-tab-ai"><Sparkles className="h-3.5 w-3.5 mr-1.5 text-amber-500" />AI Tools</TabsTrigger>
          </TabsList>

          <TabsContent value="revenue">
            <Card>
              <div className="flex items-center justify-between mb-4"><h3 className="font-display font-bold text-navy-800">Monthly signups & revenue</h3><div className="text-sm text-slate-500">Job posts: {money(stats.job_revenue)} • Memberships: {money(stats.membership_revenue)}</div></div>
              <Table>
                <TableHeader><TableRow><TableHead>Month</TableHead><TableHead>Signups</TableHead><TableHead>Transactions</TableHead><TableHead className="text-right">Revenue</TableHead></TableRow></TableHeader>
                <TableBody>
                  {stats.monthly.length === 0 ? <TableRow><TableCell colSpan={4} className="text-center text-slate-400 py-6">No data yet.</TableCell></TableRow> :
                    stats.monthly.map((m) => (
                      <TableRow key={m.month} data-testid={`revenue-row-${m.month}`}>
                        <TableCell className="font-medium">{m.month || "—"}</TableCell><TableCell>{m.signups || 0}</TableCell><TableCell>{m.count || 0}</TableCell><TableCell className="text-right font-semibold text-emerald-600">{money(m.revenue)}</TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </Card>
          </TabsContent>

          <TabsContent value="jobs">
            <Card action={<Button size="sm" variant="outline" onClick={() => exportCsv("jobs")} className="gap-1.5" data-testid="admin-export-csv-button"><Download className="h-3.5 w-3.5" /> CSV</Button>}>
              <Table>
                <TableHeader><TableRow><TableHead>Job</TableHead><TableHead>Employer</TableHead><TableHead>Status</TableHead><TableHead>Apps</TableHead><TableHead className="text-right">Moderate</TableHead></TableRow></TableHeader>
                <TableBody>
                  {jobs.map((j) => (
                    <TableRow key={j.job_id} data-testid={`admin-job-${j.job_id}`}>
                      <TableCell><div className="font-medium text-navy-800">{j.job_title}</div><div className="text-xs text-slate-400">{j.remote ? "Remote" : `${j.city}, ${j.state}`}</div></TableCell>
                      <TableCell className="text-sm text-slate-500">{j.employer_email}</TableCell>
                      <TableCell><StatusBadge s={j.status} /></TableCell>
                      <TableCell>{j.application_count}</TableCell>
                      <TableCell className="text-right space-x-1">
                        <Button size="sm" variant="ghost" title="Social kit" onClick={() => openSocial(j)} data-testid={`social-${j.job_id}`}><Share2 className="h-4 w-4 text-blue-600" /></Button>
                        {j.status !== "active" && <Button size="sm" variant="ghost" title="Publish" onClick={() => moderate(j.job_id, "active")} data-testid={`publish-${j.job_id}`}><CheckCircle2 className="h-4 w-4 text-emerald-600" /></Button>}
                        <Button size="sm" variant="ghost" title="Flag" onClick={() => moderate(j.job_id, "flagged")} data-testid={`flag-${j.job_id}`}><Flag className="h-4 w-4 text-amber-600" /></Button>
                        <Button size="sm" variant="ghost" title="Suspend" onClick={() => moderate(j.job_id, "suspended")} data-testid={`suspend-${j.job_id}`}><Ban className="h-4 w-4 text-red-600" /></Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          </TabsContent>

          <TabsContent value="users">
            <Card action={<Button size="sm" variant="outline" onClick={() => exportCsv("users")} className="gap-1.5" data-testid="admin-export-csv-users"><Download className="h-3.5 w-3.5" /> CSV</Button>}>
              <Table>
                <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Email</TableHead><TableHead>Role</TableHead><TableHead>Joined</TableHead></TableRow></TableHeader>
                <TableBody>
                  {users.map((u) => (
                    <TableRow key={u.user_id} data-testid={`admin-user-${u.user_id}`}>
                      <TableCell className="font-medium text-navy-800">{u.name}</TableCell><TableCell className="text-sm text-slate-500">{u.email}</TableCell>
                      <TableCell><Badge variant="secondary" className="uppercase text-[10px]">{u.role}</Badge></TableCell>
                      <TableCell className="text-xs text-slate-400">{u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          </TabsContent>

          <TabsContent value="payments">
            <Card action={<Button size="sm" variant="outline" onClick={() => exportCsv("payments")} className="gap-1.5" data-testid="admin-export-csv-payments"><Download className="h-3.5 w-3.5" /> CSV</Button>}>
              <Table>
                <TableHeader><TableRow><TableHead>Date</TableHead><TableHead>User</TableHead><TableHead>Purpose</TableHead><TableHead>Status</TableHead><TableHead>Amount</TableHead><TableHead className="text-right"></TableHead></TableRow></TableHeader>
                <TableBody>
                  {payments.map((p) => (
                    <TableRow key={p.session_id} data-testid={`admin-payment-${p.session_id}`}>
                      <TableCell className="text-xs text-slate-400">{p.created_at ? new Date(p.created_at).toLocaleDateString() : "—"}</TableCell>
                      <TableCell className="text-sm">{p.user_email}</TableCell>
                      <TableCell><Badge variant="secondary">{p.purpose === "job_post" ? "Job $99" : "Membership $10"}</Badge></TableCell>
                      <TableCell><StatusBadge s={p.payment_status} /></TableCell>
                      <TableCell className="font-semibold">{money(p.amount)}</TableCell>
                      <TableCell className="text-right">{p.payment_status === "paid" && p.status !== "refunded" && <Button size="sm" variant="outline" onClick={() => refund(p.session_id)} data-testid={`refund-${p.session_id}`}>Refund</Button>}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          </TabsContent>

          <TabsContent value="apps">
            <Card action={<Button size="sm" variant="outline" onClick={() => exportCsv("applications")} className="gap-1.5" data-testid="admin-export-csv-applications"><Download className="h-3.5 w-3.5" /> CSV</Button>}>
              <Table>
                <TableHeader><TableRow><TableHead>Applicant</TableHead><TableHead>Job</TableHead><TableHead>Email status</TableHead><TableHead>Date</TableHead><TableHead className="text-right"></TableHead></TableRow></TableHeader>
                <TableBody>
                  {apps.map((a) => (
                    <TableRow key={a.application_id} data-testid={`admin-app-${a.application_id}`}>
                      <TableCell className="font-medium text-navy-800">{a.full_name}</TableCell>
                      <TableCell className="text-sm text-slate-500">{a.job_title} · {a.company_name}</TableCell>
                      <TableCell>{a.email_status === "failed" ? <Badge className="bg-red-100 text-red-700">failed</Badge> : <Badge className="bg-emerald-100 text-emerald-700">sent</Badge>}</TableCell>
                      <TableCell className="text-xs text-slate-400">{new Date(a.created_at).toLocaleDateString()}</TableCell>
                      <TableCell className="text-right"><Button size="sm" variant="outline" onClick={() => resendEmail(a.application_id)} data-testid={`resend-${a.application_id}`}>Resend email</Button></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          </TabsContent>

          <TabsContent value="ai">
            <div className="grid lg:grid-cols-2 gap-4 mt-4">
              <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5" data-testid="ai-jobdesc-card">
                <div className="flex items-center gap-2 mb-1"><Sparkles className="h-4 w-4 text-amber-500" /><h3 className="font-display font-bold text-navy-800">Job description writer</h3></div>
                <p className="text-sm text-slate-500 mb-4">Turn a few notes into a polished safety job description.</p>
                <div className="grid grid-cols-2 gap-2 mb-2">
                  <Input placeholder="Job title" value={jd.job_title} onChange={(e) => setJd({ ...jd, job_title: e.target.value })} data-testid="jd-title" />
                  <Input placeholder="Company" value={jd.company} onChange={(e) => setJd({ ...jd, company: e.target.value })} data-testid="jd-company" />
                </div>
                <Input placeholder="State / Location (optional)" value={jd.state} onChange={(e) => setJd({ ...jd, state: e.target.value })} className="mb-2" data-testid="jd-state" />
                <Textarea placeholder="A few notes about the role, duties, must-have certifications…" rows={4} value={jd.notes} onChange={(e) => setJd({ ...jd, notes: e.target.value })} data-testid="jd-notes" />
                <Button onClick={genJobDescription} disabled={jdLoading} className="mt-3 bg-navy-800 hover:bg-navy-900 text-white gap-2" data-testid="jd-generate">
                  {jdLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} Generate
                </Button>
                {jdOut && (
                  <div className="mt-4">
                    <Textarea value={jdOut} readOnly rows={12} className="text-sm" data-testid="jd-output" />
                    <Button variant="outline" size="sm" className="mt-2 gap-1.5" onClick={() => copyText(jdOut)} data-testid="jd-copy"><Copy className="h-3.5 w-3.5" /> Copy</Button>
                  </div>
                )}
              </div>

              <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5" data-testid="ai-coverletter-card">
                <div className="flex items-center gap-2 mb-1"><Sparkles className="h-4 w-4 text-amber-500" /><h3 className="font-display font-bold text-navy-800">Cover letter helper</h3></div>
                <p className="text-sm text-slate-500 mb-4">Draft a tailored cover letter for a specific role.</p>
                <div className="grid grid-cols-2 gap-2 mb-2">
                  <Input placeholder="Job title" value={cl.job_title} onChange={(e) => setCl({ ...cl, job_title: e.target.value })} data-testid="cl-title" />
                  <Input placeholder="Company" value={cl.company} onChange={(e) => setCl({ ...cl, company: e.target.value })} data-testid="cl-company" />
                </div>
                <Textarea placeholder="Applicant background: experience, certifications (OSHA 30, CSP…), strengths…" rows={5} value={cl.background} onChange={(e) => setCl({ ...cl, background: e.target.value })} data-testid="cl-background" />
                <Button onClick={genCoverLetter} disabled={clLoading} className="mt-3 bg-navy-800 hover:bg-navy-900 text-white gap-2" data-testid="cl-generate">
                  {clLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} Generate
                </Button>
                {clOut && (
                  <div className="mt-4">
                    <Textarea value={clOut} readOnly rows={12} className="text-sm" data-testid="cl-output" />
                    <Button variant="outline" size="sm" className="mt-2 gap-1.5" onClick={() => copyText(clOut)} data-testid="cl-copy"><Copy className="h-3.5 w-3.5" /> Copy</Button>
                  </div>
                )}
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      <Dialog open={!!social} onOpenChange={(v) => !v && setSocial(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="font-display">Social kit — {social?.job?.job_title}</DialogTitle></DialogHeader>
          {social && (
            <div className="space-y-4">
              <div>
                <label className="text-sm font-semibold text-navy-800">Seasonal theme</label>
                <Select value={social.theme} onValueChange={(v) => setSocial((s) => ({ ...s, theme: v }))}>
                  <SelectTrigger className="mt-1.5" data-testid="social-theme-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["default", "newyear", "spring", "summer", "independence", "fall", "holiday"].map((t) => (
                      <SelectItem key={t} value={t}>{t === "default" ? "Brand (default)" : t.charAt(0).toUpperCase() + t.slice(1)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <img src={`${API}/admin/social/job/${social.job.job_id}.png?theme=${social.theme}`} alt="Social card" className="w-full rounded-xl border border-slate-200 shadow-sm" data-testid="social-image-preview" />
              <div>
                <label className="text-sm font-semibold text-navy-800">Caption</label>
                <Textarea value={social.caption} readOnly rows={7} className="mt-1.5 text-sm" data-testid="social-caption" />
              </div>
              <div className="flex gap-2">
                <Button onClick={() => { navigator.clipboard.writeText(social.caption); toast.success("Caption copied"); }} data-testid="copy-caption-btn" className="bg-navy-800 hover:bg-navy-900 text-white flex-1">Copy caption</Button>
                <Button variant="outline" onClick={() => window.open(`${API}/admin/social/job/${social.job.job_id}.png?theme=${social.theme}`, "_blank")} data-testid="download-social-btn" className="flex-1 gap-1.5"><ImageDown className="h-4 w-4" /> Open image</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
  );
}

const Stat = ({ icon: Icon, label, value, accent }) => (
  <div className={`rounded-xl border p-5 shadow-sm flex items-center gap-4 ${accent ? "bg-navy-800 border-navy-800 text-white" : "bg-white border-slate-200"}`}>
    <div className={`h-11 w-11 rounded-lg flex items-center justify-center ${accent ? "bg-amber-500/20" : "bg-amber-100"}`}><Icon className={`h-5 w-5 ${accent ? "text-amber-400" : "text-amber-600"}`} /></div>
    <div><div className="font-display text-2xl font-extrabold">{value}</div><div className={`text-xs uppercase tracking-wide font-semibold ${accent ? "text-slate-300" : "text-slate-500"}`}>{label}</div></div>
  </div>
);

const Card = ({ children, action }) => (
  <div className="bg-white rounded-xl border border-slate-200 shadow-sm mt-4 p-4 sm:p-6">
    {action && <div className="flex justify-end mb-3">{action}</div>}
    <div className="overflow-auto">{children}</div>
  </div>
);

const StatusBadge = ({ s }) => {
  const map = { active: "bg-emerald-100 text-emerald-700", paid: "bg-emerald-100 text-emerald-700", draft: "bg-slate-100 text-slate-600", pending: "bg-slate-100 text-slate-500", expired: "bg-slate-100 text-slate-400", suspended: "bg-red-100 text-red-700", flagged: "bg-amber-100 text-amber-700", refunded: "bg-purple-100 text-purple-700" };
  return <Badge className={`${map[s] || "bg-slate-100"} hover:${map[s]}`}>{s}</Badge>;
};
